// src/components/settings/shared/SettingsElements.tsx

import React from 'react';
import { IconButton, Chip, Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  WarningAmber as WarningIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { colors } from '../../../theme/themeTypes';
import DialogBase from '../../common/modal/DialogBase';
import { ModalCancelButton, ModalDeleteButton } from '../../common/Buttons';

interface ConfirmDeleteDialogProps {
  open: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLabel?: string;
}

interface DialogDividerProps {
  marginTop?: number | string;
  paddingTop?: number | string;
}

export const ConfirmDeleteDialog: React.FC<ConfirmDeleteDialogProps> = ({
  open,
  title,
  message,
  onConfirm,
  onCancel,
  confirmLabel = 'Delete',
}) => (
  <DialogBase
    draggable
    open={open}
    onClose={onCancel}
    title={title}
    headerIcon={<WarningIcon />}
    headerColor="secondary"
    footer={
      <>
        <ModalCancelButton
          onClick={onCancel}
          sx={{
            minWidth: { xs: 'auto', sm: '160px' },
            borderColor: colors.secondary.main,
            color: colors.secondary.main,
            '&:hover': {
              letterSpacing: 'normal',
              backgroundColor: colors.secondary.light,
            },
            '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
          }}
        />
        <ModalDeleteButton
          onClick={onConfirm}
          text={confirmLabel}
          sx={{
            minWidth: { xs: 'auto', sm: '160px' },
            '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
          }}
        />
      </>
    }
  >
    <Typography sx={{ fontSize: '2rem', textAlign: 'center' }}>
      {message}
    </Typography>
  </DialogBase>
);

export const SettingsEditButton = styled(IconButton)(({ theme }) => ({
  color: colors.primary.main,
  transition:
    'transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease',
  '& svg': { fontSize: '2.5rem' },
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    transform: 'scale(1.15)',
    boxShadow:
      theme.palette.mode === 'dark'
        ? `0 0 0 4px ${theme.palette.grey[900]}, 0 0 0 6px ${theme.palette.primary.light}, 0 0 12px 6px ${theme.palette.primary.light}40`
        : `0 0 0 4px #fff, 0 0 0 6px ${theme.palette.primary.main}, 0 0 12px 6px ${theme.palette.primary.main}35`,
    '& svg': {
      color: '#fff',
    },
  },
}));

export const SettingsDeleteButton = styled(IconButton)(({ theme }) => ({
  color: colors.secondary.main,
  transition:
    'transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease',
  '&.Mui-disabled': { color: colors.grey[500] },
  '& svg': { fontSize: '2.5rem' },
  '&:hover': {
    backgroundColor: theme.palette.secondary.main,
    transform: 'scale(1.15)',
    boxShadow:
      theme.palette.mode === 'dark'
        ? `0 0 0 4px ${theme.palette.grey[900]}, 0 0 0 6px ${theme.palette.secondary.light}, 0 0 12px 6px ${theme.palette.primary.light}40`
        : `0 0 0 4px #fff, 0 0 0 6px ${theme.palette.secondary.main}, 0 0 12px 6px ${theme.palette.secondary.main}35`,
    '& svg': {
      color: '#fff',
    },
  },
}));

export const SettingsRestoreButton = styled(IconButton)(({ theme }) => ({
  color: colors.primary.main,
  transition:
    'transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease',
  '&.Mui-disabled': { color: colors.grey[500] },
  '& svg': { fontSize: '2.5rem' },
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    transform: 'scale(1.15)',
    boxShadow:
      theme.palette.mode === 'dark'
        ? `0 0 0 4px ${theme.palette.grey[900]}, 0 0 0 6px ${theme.palette.primary.light}, 0 0 12px 6px ${theme.palette.primary.light}40`
        : `0 0 0 4px #fff, 0 0 0 6px ${theme.palette.primary.main}, 0 0 12px 6px ${theme.palette.primary.main}35`,
    '& svg': {
      color: '#fff',
    },
  },
}));

export const SettingsPermanentDeleteButton = styled(IconButton)(
  ({ theme }) => ({
    color: colors.secondary.main,
    transition:
      'transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease',
    '&.Mui-disabled': { color: colors.grey[500] },
    '& svg': { fontSize: '2.5rem' },
    '&:hover': {
      backgroundColor: theme.palette.secondary.main,
      transform: 'scale(1.15)',
      boxShadow:
        theme.palette.mode === 'dark'
          ? `0 0 0 4px ${theme.palette.grey[900]}, 0 0 0 6px ${theme.palette.secondary.light}, 0 0 12px 6px ${theme.palette.secondary.light}40`
          : `0 0 0 4px #fff, 0 0 0 6px ${theme.palette.secondary.main}, 0 0 12px 6px ${theme.palette.secondary.main}35`,
      '& svg': {
        color: '#fff',
      },
    },
  })
);

export const SettingsChip = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  '& .MuiChip-root': {
    padding: '1.4rem 0.25rem',
  },
  '& .MuiChip-label': {
    fontSize: '1.5rem',
    fontWeight: 500,
  },
});

export const ChipCell = styled(Box)({
  '&&': {
    justifyContent: 'center',
  },
  '& .MuiChip-root': {
    padding: '1.5rem 0.25rem',
  },
  '& .MuiChip-label': {
    fontSize: '1.5rem',
  },
});

export const DialogDivider = styled(Box)({
  borderTop: `3px dashed ${colors.grey[400]}`,
});

export const RefCountChip: React.FC<{ count: number }> = ({ count }) => (
  <SettingsChip>
    <Chip
      label={count}
      size="small"
      color={count > 0 ? 'primary' : 'default'}
      variant={count > 0 ? 'filled' : 'outlined'}
      sx={{ border: `3px solid ${colors.primary.main}` }}
    />
  </SettingsChip>
);

export const DeletedChip = () => (
  <SettingsChip>
    <Chip
      label="Deleted"
      size="small"
      variant="filled"
      sx={{
        border: `3px solid ${colors.secondary.main}`,
        color: colors.secondary.main,
        backgroundColor: colors.secondary.light,
      }}
    />
  </SettingsChip>
);

export const SystemChip = () => (
  <SettingsChip>
    <Chip
      icon={<LockIcon sx={{ fontSize: '1.6rem !important' }} />}
      label="Standard"
      size="small"
      variant="outlined"
      sx={{
        border: `3px solid ${colors.grey[500]}`,
        color: colors.grey[700],
      }}
    />
  </SettingsChip>
);

export const AdminChip = () => (
  <SettingsChip>
    <Chip
      label="Admin"
      size="small"
      variant="filled"
      sx={{
        border: `3px solid ${colors.secondary.main}`,
        color: colors.grey[0],
        backgroundColor: colors.secondary.main,
      }}
    />
  </SettingsChip>
);

export const ManagerChip = () => (
  <SettingsChip>
    <Chip
      label="Manager"
      size="small"
      variant="filled"
      sx={{
        border: `3px solid ${colors.tertiary.main}`,
        color: colors.grey[0],
        backgroundColor: colors.tertiary.main,
      }}
    />
  </SettingsChip>
);

export const StaffChip = () => (
  <SettingsChip>
    <Chip
      label="Staff"
      size="small"
      variant="filled"
      sx={{
        border: `3px solid ${colors.primary.main}`,
        color: colors.grey[0],
        backgroundColor: colors.primary.main,
      }}
    />
  </SettingsChip>
);

export const KennelCountChip: React.FC<{ count: number }> = ({ count }) => (
  <SettingsChip>
    <Chip
      label={count}
      size="small"
      color={count > 0 ? 'primary' : 'default'}
      variant={count > 0 ? 'filled' : 'outlined'}
      sx={{ border: `3px solid ${colors.primary.main}` }}
    />
  </SettingsChip>
);
